import * as React from 'react';

import { GoogleMap } from '@react-google-maps/api';
import { useDispatch, useSelector } from 'react-redux';
import { ReduxState } from 'ducks';

import config from 'config';
import { useGoogleMapsApi } from 'hooks/useGoogleMapsApi';
import './Map-global.css';
import styles from './Map.module.css';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { SchedulePane } from './SchedulePane/SchedulePane';
import { SearchContext } from './SearchContext';
import { IllustrationOverlay } from './IlustrationOverlay/IllustrationOverlay';
import { CurrentPositionMarker } from './CurrentPositionMarker';
import { CurrentLocationButton } from './CurrentLocationButton';
import { MapDisplayContextProvider, RestaurantPage } from './MapDisplayContext';
import { SearchResults } from './SearchResults/SearchResults';
import { PinDetails } from './PinDetails/PinDetails';
import { FilterPane } from 'components/Map/FilterPane/FilterPane';
import { SearchInput } from './SearchInput/SearchInput';
import { MyAccount } from './MyAccount/MyAccount';
import { Tickets } from './Tickets/Tickets';
import { ProductSummary } from 'models/product';
import { Checkout } from './Checkout/Checkout';
import { StampModal } from './StampModal';
import { StampRallyModal } from 'components/StampRallyModal/StampRallyModal';
import { InstantWinPopupStampRally } from 'components/InstantWinPopup/InstantWinPopupStampRally';
import { GuidancePageCoupon } from 'models/guidancePage';
import { CouponModal } from './CouponModal/CouponModal';
import { Pins } from './Pins';
import { MarketingAutomationCampaignFetcher } from './MarketingAutomationCampaignFetcher';
import { Header } from './Header/Header';
import { Notifications } from './Notifications/Notifications';
import { CheckoutFormValues } from './Checkout/formValues';
import { SavedDisplayStateLoader } from './SavedDisplayStateLoader';
import { DigitalMapPin } from 'models/digitalMap';
import { CurrentPositionProvider } from 'components/CurrentPositionProvider/CurrentPositionProvider';
import { LocationLogger } from './LocationLogger';
import { RestaurantDetails } from './RestaurantDetails/RestaurantDetails';
import { useExistingReservation } from './useExistingReservation';
import { useCustomerToken } from 'hooks/useCustomerToken';
import { lookupReservationsByAccessToken } from 'ducks/client/reservation';
import { useTranslation } from 'react-i18next';
import { ApiKeyContext } from 'contexts/ApiKeyContext';
import { getCommonRedirectUri } from 'lib/util/customer';
import moment from 'moment';
import { setMarketingAutomationCampaignReservation } from 'ducks/client/marketingAutomationCampaigns';
import { Chat } from 'components/Chat/Chat';

import { getSolidBackgroundMapType } from './getSolidBackgroundMapType';
import { getTiledMapType } from './getTiledMapType';
import { SingleCategorySelect } from './SingleCategorySelect/SingleCategorySelect';
import { MediaPreloader } from './MediaPreloader';
import { DesktopSearchResults } from './DesktopSearchResults/DesktopSearchResults';
import { DirectionsFooterBar } from './DirectionsFooterBar/DirectionsFooterBar';
import { CouponBottomSheet } from './CouponBottomSheet/CouponBottomSheet';
import { refreshDigitalMap, selectDigitalMap } from 'ducks/universal/digitalMap';
import { useRouter } from 'next/router';
import { WaitTimeUpdateNotifier } from './WaitTimeUpdateNotifier';
import OrdersPage from './Orders/OrdersPage/OrdersPage';
import { fetchAllRestaurantOrdersByAccessToken } from 'ducks/client/restaurantOrders';
import { removeNotifications } from 'ducks/client/notification';
import { StampRallyFetcher } from './StampRallyFetcher';
import { MapHead } from './MapHead';
import { FloorSelector } from './FloorSelector/FloorSelector';
import { getFloorOverlay, isItineraryCreatorEnabled } from './util';
import { OpenTableReservation } from './OpenTableReservation/OpenTableReservation';
import { QRCodeButton } from './QRCodeButton/QRCodeButton';
import { IframeDisplay } from './IframeDisplay/IframeDisplay';
import { Itineraries } from './Itineraries/Itineraries';
import { Itinerary } from 'models/itinerary';
import { smoothFitCoordinates } from './mapUtils';
import { WalkTimeOverlay } from './Itineraries/WalkTimeOverlay';
import { useItineraries } from './useItineraries';
import { StarredPinList } from './StarredPins/StarredPinList';
import { ItineraryFloatingButton } from './ItineraryFloatingButton';
import { ItinerarySideModal } from './ItinerarySideModal/ItinerarySideModal';
import { useVisitorIdentifier } from 'hooks/useVisitorIdentifier';
import { fetchRedeemedCouponIds } from 'ducks/client/coupons';
import { BusRoutes } from './BusRoutes/BusRoutes';
import OLMap from 'ol/Map';
import { OpenLayersMap } from './OpenLayers/OpenLayersMap';
import { useMapDisplayContext } from './MapDisplayContext';
import { useOnlineStatus } from 'hooks/useOnlineStatus';
import { InstallButton } from './InstallButton/InstallButton';
import { useIsSmallScreen } from './useIsSmallScreen';
import { PinList } from './PinList/PinList';
import { ItineraryRebaser } from './ItineraryRebaser';
import { ItemSelection } from './RestaurantDetails/RestaurantDetails';

export interface DirectionsRoute {
  outlinePolyline: google.maps.Polyline | null;
  pathPolyline: google.maps.Polyline | null;
  distanceInMeters: number;
  timeInSeconds: number;
  destinationPinName: string;
}

interface MapProps {
  hideHeader?: boolean;
  headerHeight?: number;
  bottomNavHeight?: number;
  hideChatButton?: boolean;
  containerStyle?: React.CSSProperties;
}

// Map wraps MapPage so that we can quickly reset map state via a React key prop when the map changes.
// Map might change from one map to another, e.g. when the user changes language.
export const Map = ({
  hideHeader,
  headerHeight,
  bottomNavHeight,
  hideChatButton,
  containerStyle,
}: MapProps) => {
  const digitalMap = useSelector(selectDigitalMap);

  return (
    <MapPage
      key={digitalMap?.id}
      hideHeader={hideHeader}
      headerHeight={headerHeight}
      bottomNavHeight={bottomNavHeight}
      hideChatButton={hideChatButton}
      containerStyle={containerStyle}
    />
  );
};

// Add this helper function outside the component
const calculateMidpoint = (p1: google.maps.LatLng, p2: google.maps.LatLng): google.maps.LatLng => {
  return new google.maps.LatLng((p1.lat() + p2.lat()) / 2, (p1.lng() + p2.lng()) / 2);
};

// Add this interface near the top of the file
interface ItineraryPolyline {
  polyline: google.maps.Polyline;
  date: string;
}

const MapPage = ({
  hideHeader,
  headerHeight,
  bottomNavHeight,
  hideChatButton,
  containerStyle,
}: MapProps) => {
  const directionsService = React.useRef<google.maps.DirectionsService | null>(null);
  const directionsRenderer = React.useRef<google.maps.DirectionsRenderer | null>(null);
  const [directionsRoute, setDirectionsRoute] = React.useState<DirectionsRoute | null>(null);

  const { isLoaded: isGoogleMapsLoaded } = useGoogleMapsApi();
  const { i18n } = useTranslation();
  const router = useRouter();
  const reservationId = router.query.r as string;

  const [selectedFloor, setSelectedFloor] = React.useState(1);
  const [shouldFetchCustomerReservations, setShouldFetchCustomerReservations] = React.useState(
    true
  );
  const [displayMode, setDisplayMode] = React.useState<
    'BASE' | 'SEARCH_RESULTS' | 'SEARCH_INPUT' | 'DESKTOP_SEARCH_RESULTS' | 'SHOW_DIRECTIONS_ROUTE'
  >('BASE');
  const [searchText, setSearchText] = React.useState('');
  const [sortBy, setSortBy] = React.useState<'RELEVANCE' | 'DISTANCE'>('RELEVANCE');
  const [recentSearchQueries, setRecentSearchQueries] = React.useState<string[]>([]);
  const [googleMap, setGoogleMap] = React.useState<google.maps.Map | null>(null);
  const [openLayersMap, setOpenLayersMap] = React.useState<OLMap | null>(null);
  const [showFilterPane, setShowFilterPane] = React.useState(false);
  const [searchResultsListPosition, setSearchResultsListPosition] = React.useState<
    'MINIMIZED' | 'MIDWAY' | 'MAXIMIZED'
  >('MINIMIZED');
  const [productInCart, setProductInCart] = React.useState<ProductSummary | null>(null);
  const [savedCartParams, setSavedCartParams] = React.useState<CheckoutFormValues | null>(null);
  const [activeStampPinIdx, setActiveStampPinIdx] = React.useState<number | null>(null);
  const [openStampRallyModal, setOpenStampRallyModal] = React.useState(false);
  const [activeCouponViewContext, setActiveCouponViewContext] = React.useState<{
    coupon: GuidancePageCoupon;
    pin: DigitalMapPin;
  } | null>(null);
  const [menuExpanded, setMenuExpanded] = React.useState<boolean>(false);
  const [activeRestaurantId, setActiveRestaurantId] = React.useState<string | null>(null);
  const [activeOpenTableRestaurantId, setActiveOpenTableRestaurantId] = React.useState<
    string | null
  >(null);
  const [orderStatusRestaurantId, setOrderStatusRestaurantId] = React.useState<string | null>(null);
  const [orderStatusRestaurantOrderId, setOrderStatusRestaurantOrderId] = React.useState<
    string | null
  >(null);
  const [shouldFetchRestaurantOrders, setShouldFetchRestaurantOrders] = React.useState(true);
  const [activeIframeUrl, setActiveIframeUrl] = React.useState<string | null>(null);
  const [selectedItineraryDate, setSelectedItineraryDate] = React.useState<string | null>(null);
  const [activeItineraryPinKeys, setActiveItineraryPinKeys] = React.useState<string[]>([]);
  const [newItinerary, setNewItinerary] = React.useState<Itinerary | null>(null);
  const [showStarredPinList, setShowStarredPinList] = React.useState(false);
  const [showItinerarySideModal, setShowItinerarySideModal] = React.useState(false);
  const { trackingId } = useVisitorIdentifier();

  // PWA
  const [deferredInstallPrompt, setDeferredInstallPrompt] = React.useState<Event | null>(null);
  const [isIOS, setIsIOS] = React.useState(false);
  const [isPWAInstalled, setIsPWAInstalled] = React.useState(false);
  const [isStandalone, setIsStandalone] = React.useState(false);

  const digitalMap = useSelector((state: ReduxState) => state.universal.digitalMap.map);

  const stampRally = useSelector(
    (state: ReduxState) => state.universal.digitalGuidanceStampRally.stampRally
  );

  const isSmallScreen = useIsSmallScreen();

  const [activeItemSelection, setActiveItemSelection] = React.useState<ItemSelection | null>(null);
  const [activeRestaurantPageStack, setActiveRestaurantPageStack] = React.useState<
    RestaurantPage[]
  >(['MAIN']);

  React.useEffect(() => {
    window.addEventListener('beforeinstallprompt', (e) => {
      e.preventDefault();
      setDeferredInstallPrompt(e);
    });

    // navigator is undefined in server so we check in useEffect
    setIsIOS(/iPad|iPhone/.test(navigator.userAgent));

    setIsStandalone(
      window.matchMedia('(display-mode: standalone)').matches || (navigator as any).standalone
    );
    window.addEventListener('resize', () => {
      setIsStandalone(
        window.matchMedia('(display-mode: standalone)').matches || (navigator as any).standalone
      );

      (async () => {
        const nav = navigator as any;
        if (nav.getInstalledRelatedApps) {
          const relatedApps = await nav.getInstalledRelatedApps();
          setIsPWAInstalled(relatedApps.some((app: any) => app.platform === 'webapp'));
        }
      })();
    });

    window.addEventListener('appinstalled', () => {
      setIsPWAInstalled(true);
    });
  }, []);

  const {
    activePage,
    activePinKey,
    pinInfoDisplayStatus,
    setActivePage,
    setPinInfoDisplayState,
    replaceUrlParams,
    pushUrlParams,
    popUrlParams,
  } = useMapDisplayContext();

  const { accessToken, idProvider } = useCustomerToken();
  const dispatch = useDispatch();
  const { apiKey } = React.useContext(ApiKeyContext);

  const restaurantOrderNotifications = useSelector(
    (state: ReduxState) => state.notification.restaurantOrderNotifications
  );

  // Modify the ref to store multiple polylines
  const polylinesRef = React.useRef<ItineraryPolyline[]>([]);

  // Update the state type
  const [walkTimeOverlays, setWalkTimeOverlays] = React.useState<
    Array<{
      position: google.maps.LatLng;
      walkTime: number;
      id: string;
    }>
  >([]);

  const isOnline = useOnlineStatus();

  React.useEffect(() => {
    if (restaurantOrderNotifications.length > 0) {
      setShouldFetchRestaurantOrders(true);
      dispatch(
        removeNotifications(restaurantOrderNotifications.map((notification) => notification.id))
      );
    }
  }, [restaurantOrderNotifications, dispatch]);

  React.useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Refresh digital map data when page becomes visible; for example, when the
        // device is unlocked after being locked.
        if (digitalMap?.id) {
          dispatch(refreshDigitalMap(apiKey, digitalMap.id, i18n.language, reservationId));
        }
      }
    };

    // Add event listener
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [apiKey, digitalMap?.id, dispatch, i18n.language, reservationId]);

  React.useEffect(() => {
    if (accessToken && shouldFetchCustomerReservations) {
      dispatch(
        lookupReservationsByAccessToken(
          apiKey,
          i18n.language,
          accessToken,
          '',
          idProvider ?? '',
          getCommonRedirectUri(),
          ['CONFIRMED'],
          moment().add(-7, 'days').format('YYYY-MM-DD'),
          undefined
        )
      );

      setShouldFetchCustomerReservations(false);
    }
  }, [accessToken, i18n.language, apiKey, dispatch, idProvider, shouldFetchCustomerReservations]);

  React.useEffect(() => {
    if (accessToken && shouldFetchRestaurantOrders) {
      dispatch(
        fetchAllRestaurantOrdersByAccessToken(
          apiKey,
          i18n.language,
          accessToken,
          '',
          idProvider ?? '',
          getCommonRedirectUri()
        )
      );

      setShouldFetchRestaurantOrders(false);
    }

    // Set up visibility change listener to refresh restaurant orders when the page becomes visible
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        if (accessToken) {
          dispatch(
            fetchAllRestaurantOrdersByAccessToken(
              apiKey,
              i18n.language,
              accessToken,
              '',
              idProvider ?? '',
              getCommonRedirectUri()
            )
          );
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [accessToken, i18n.language, apiKey, dispatch, idProvider, shouldFetchRestaurantOrders]);

  React.useEffect(() => {
    if (trackingId) {
      dispatch(fetchRedeemedCouponIds(apiKey, reservationId, trackingId));
    }
  }, [trackingId, dispatch, apiKey, reservationId]);

  const background = digitalMap?.background;
  const existingReservation = useExistingReservation();
  const shouldShowOverlay = background?.image_url;

  const { selectedItinerary } = useItineraries();

  React.useEffect(() => {
    if (existingReservation) {
      dispatch(setMarketingAutomationCampaignReservation(existingReservation));
    }
  }, [existingReservation, dispatch]);

  const onLoad = React.useCallback(
    (map: google.maps.Map) => {
      setGoogleMap(map);
      if (digitalMap) {
        if (
          !digitalMap?.background?.do_not_use_overlay_image &&
          digitalMap?.background?.use_overlay_tiling
        ) {
          // Create the tiled overlay
          const tiledOverlay = getTiledMapType(digitalMap, selectedFloor);

          // Add it as an overlay map type
          map.overlayMapTypes.push(tiledOverlay);
        }

        // Set the base map type
        map.mapTypes.set('solid', getSolidBackgroundMapType(digitalMap));
      }

      directionsService.current = new google.maps.DirectionsService();
      directionsRenderer.current = new google.maps.DirectionsRenderer({
        map,
        suppressMarkers: true,
        polylineOptions: {
          strokeOpacity: 0,
          strokeWeight: 0,
          zIndex: 0,
        },
      });

      (map as any).setHeadingInteractionEnabled(false);

      map.setCenter({
        lat: digitalMap?.default_map_center.latitude ?? 0,
        lng: digitalMap?.default_map_center.longitude ?? 0,
      });
    },
    [digitalMap, selectedFloor]
  );

  React.useEffect(() => {
    if (
      googleMap &&
      !digitalMap?.background?.do_not_use_overlay_image &&
      digitalMap?.background?.use_overlay_tiling
    ) {
      // Clear existing overlay map types
      while (googleMap.overlayMapTypes.getLength() > 0) {
        googleMap.overlayMapTypes.pop();
      }
      // Add new overlay for current floor
      googleMap.overlayMapTypes.push(getTiledMapType(digitalMap, selectedFloor));
    }
  }, [googleMap, digitalMap, selectedFloor]);

  const onGetDirections = React.useCallback(
    async (origin: google.maps.LatLng, destinationPin: DigitalMapPin) => {
      const destination = new google.maps.LatLng(
        destinationPin.location.latitude,
        destinationPin.location.longitude
      );

      try {
        const resp = await directionsService.current?.route({
          origin,
          destination,
          travelMode: google.maps.TravelMode.WALKING,
        });

        if (resp) {
          // Note: directionsRenderer.setDirections() is used for centering directions. Drawing of the
          // directions is handled by the polyline below.
          directionsRenderer.current?.setDirections(resp);
        }

        const path = [
          new google.maps.LatLng(origin.lat(), origin.lng()),
          ...google.maps.geometry.encoding.decodePath(resp?.routes?.[0]?.overview_polyline ?? ''),
          new google.maps.LatLng(destination.lat(), destination.lng()),
        ];

        const distanceInMeters = resp?.routes?.[0]?.legs?.[0]?.distance?.value ?? 0;
        const timeInSeconds = resp?.routes?.[0]?.legs?.[0]?.duration?.value ?? 0;

        setDirectionsRoute({
          destinationPinName: destinationPin.title,
          distanceInMeters,
          timeInSeconds,
          outlinePolyline: new google.maps.Polyline({
            path,
            map: googleMap,
            strokeColor: '#4284F0',
            strokeOpacity: 1.0,
            strokeWeight: 12,
            zIndex: 1,
          }),
          pathPolyline: new google.maps.Polyline({
            path,
            map: googleMap,
            strokeColor: '#3AA0FF',
            strokeOpacity: 1.0,
            strokeWeight: 10,
            zIndex: 2,
          }),
        });

        if (resp?.routes?.[0]?.bounds && googleMap) {
          googleMap.fitBounds(resp.routes[0].bounds);
        }

        pushUrlParams({ pin: destinationPin.key, pind: 'HIDDEN', nav: 'map' });
        setDisplayMode('SHOW_DIRECTIONS_ROUTE');

        (googleMap as any).setHeadingInteractionEnabled(true);
      } catch (e) {
        console.log('directions failed: ', e);
      }
    },
    [googleMap]
  );

  React.useEffect(() => {
    if (googleMap && digitalMap) {
      if (digitalMap.map_rotation) {
        googleMap?.setHeading(digitalMap.map_rotation);
      }

      const mapCapabilities = googleMap.getMapCapabilities();

      if (!mapCapabilities.isAdvancedMarkersAvailable) {
        console.warn('Advanced markers are not available on this map.');
      }
    }
  }, [googleMap, digitalMap]);

  const onClickPin = React.useCallback(
    (pinKey: string) => {
      if (activePage === 'ITINERARIES.CREATE' || activePage === 'ITINERARIES.EDIT_DETAILS') {
        if (activeItineraryPinKeys.includes(pinKey)) {
          setActiveItineraryPinKeys(activeItineraryPinKeys.filter((key) => key !== pinKey));
        } else {
          setActiveItineraryPinKeys([...activeItineraryPinKeys, pinKey]);
        }

        return;
      }

      if (activePage === 'ITINERARIES.DETAILS' && selectedItinerary) {
        const clickedItineraryDate = selectedItinerary.date_itineraries.find((dateItinerary) =>
          dateItinerary.items?.some((item) => item.pin_key === pinKey)
        )?.date;
        if (clickedItineraryDate) {
          setSelectedItineraryDate(clickedItineraryDate);
        }

        return;
      }

      if (displayMode === 'SHOW_DIRECTIONS_ROUTE') {
        replaceUrlParams({ pin: undefined, pind: 'HIDDEN' });
        return;
      }

      let usePush = true;
      if (activePinKey) {
        usePush = false;
      }
      if (displayMode === 'SEARCH_RESULTS' || !isSmallScreen) {
        if (usePush) {
          pushUrlParams({ pin: pinKey, pind: 'DETAILS' });
        } else {
          replaceUrlParams({ pin: pinKey, pind: 'DETAILS' });
        }
      } else {
        if (usePush) {
          pushUrlParams({ pin: pinKey, pind: 'SUMMARY' });
        } else {
          replaceUrlParams({ pin: pinKey, pind: 'SUMMARY' });
        }
      }
    },
    [
      displayMode,
      isSmallScreen,
      pushUrlParams,
      activePage,
      activeItineraryPinKeys,
      activePinKey,
      selectedItinerary,
      replaceUrlParams,
    ]
  );

  // Auto-expand pin details on large screens
  React.useEffect(() => {
    if (!isSmallScreen && activePinKey && pinInfoDisplayStatus === 'SUMMARY') {
      replaceUrlParams({ pin: activePinKey, pind: 'DETAILS' });
    }
  }, [activePinKey, pinInfoDisplayStatus, replaceUrlParams, isSmallScreen]);

  const onClickMap = React.useCallback(() => {
    if (directionsRoute) {
      return;
    }
    setPinInfoDisplayState({
      pinKey: null,
      displayState: 'HIDDEN',
    });
    if (displayMode === 'SEARCH_RESULTS') {
      setSearchResultsListPosition('MINIMIZED');
    }
  }, [directionsRoute, displayMode, setPinInfoDisplayState]);

  const mapDefaultOptions = React.useMemo(
    () => ({
      mapTypeControl: false,
      fullscreenControl: false,
      zoomControl: false,
      streetViewControl: false,
      disableDefaultUI: true,
      heading: digitalMap?.map_rotation ?? 0,
      mapId: digitalMap?.display_settings?.custom_google_map_id
        ? digitalMap?.display_settings?.custom_google_map_id
        : digitalMap?.background?.do_not_use_overlay_image
        ? '998d8780ce687665'
        : 'ae93e5105a43cc74',
      mapTypeId:
        digitalMap?.background?.do_not_use_overlay_image ||
        digitalMap?.display_settings?.custom_google_map_id
          ? 'roadmap'
          : 'solid',
      zoom: digitalMap?.default_map_zoom ?? 0,
      minZoom: (digitalMap?.default_map_zoom ?? 0) - 2,
    }),
    [digitalMap]
  );

  const activePin = digitalMap?.pins.find((pin) => pin.key === activePinKey) ?? null;
  const activeStampPin = digitalMap?.pins.find((pin) => pin.index === activeStampPinIdx) ?? null;

  const overlay = getFloorOverlay(digitalMap, selectedFloor);

  // Replace the existing itinerary effect with this updated version
  React.useEffect(() => {
    // Clean up existing polylines
    polylinesRef.current.forEach(({ polyline }) => {
      polyline.setMap(null);
    });
    polylinesRef.current = [];

    // Clear overlays
    setWalkTimeOverlays([]);

    if (
      !selectedItinerary ||
      !digitalMap ||
      (!googleMap && !openLayersMap) ||
      activePage !== 'ITINERARIES.DETAILS'
    ) {
      return;
    }

    // Create polylines for all dates
    selectedItinerary.date_itineraries.forEach((dateItinerary) => {
      const isActiveDate = dateItinerary.date === selectedItineraryDate;

      // Create array of pin coordinates in order
      const path = dateItinerary.items
        .map((item) => {
          const pin = digitalMap.pins.find((p) => p.key === item.pin_key);
          if (!pin) return null;
          return new google.maps.LatLng(pin.location.latitude, pin.location.longitude);
        })
        .filter((coord): coord is google.maps.LatLng => coord !== null);

      if (path.length > 0 && googleMap) {
        // Only create polylines for Google Maps
        // Create the polyline
        const polyline = new google.maps.Polyline({
          path,
          map: googleMap,
          strokeOpacity: 0,
          strokeWeight: 3,
          icons: [
            {
              icon: {
                path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                fillColor: isActiveDate ? '#3aa0ff' : '#FFFFFF',
                fillOpacity: isActiveDate ? 1 : 0.8,
                scale: isActiveDate ? 2 : 1.5,
                strokeColor: isActiveDate ? '#3aa0ff' : '#FFFFFF',
                strokeOpacity: isActiveDate ? 1 : 0.8,
              },
              offset: '0',
              repeat: '20px',
            },
          ],
          zIndex: isActiveDate ? 2 : 1,
        });

        polylinesRef.current.push({
          polyline,
          date: dateItinerary.date,
        });

        // Only create walk time overlays for the active date and Google Maps
        if (isActiveDate) {
          const newOverlays = path
            .slice(0, -1)
            .filter((_, i) => dateItinerary.items[i + 1].travel_time)
            .map((point, i) => {
              const midpoint = calculateMidpoint(point, path[i + 1]);
              const walkTime = parseInt(dateItinerary.items[i + 1].travel_time.replace('m', ''));

              return {
                position: midpoint,
                walkTime: isNaN(walkTime) ? 0 : walkTime,
                id: `${i}-${walkTime}`,
              };
            });

          setWalkTimeOverlays(newOverlays);
        }
      }
    });

    // If there's an active date, fit bounds to show it
    if (selectedItineraryDate) {
      const activeDate = selectedItinerary.date_itineraries.find(
        (date) => date.date === selectedItineraryDate
      );
      if (activeDate) {
        const coordinates = activeDate.items
          .map((item) => {
            const pin = digitalMap.pins.find((p) => p.key === item.pin_key);
            if (!pin) return null;
            return [pin.location.latitude, pin.location.longitude] as [number, number];
          })
          .filter((coord): coord is [number, number] => coord !== null);

        smoothFitCoordinates(
          googleMap,
          openLayersMap,
          coordinates,
          digitalMap?.default_map_zoom ?? 15
        );
      }
    }

    return () => {
      polylinesRef.current.forEach(({ polyline }) => {
        polyline.setMap(null);
      });
      polylinesRef.current = [];
      setWalkTimeOverlays([]);
    };
  }, [activePage, selectedItinerary, selectedItineraryDate, digitalMap, googleMap, openLayersMap]);

  // When all right side buttons are activated, the order from bottom to top is as follows:
  // 1. Chat button
  // 2. Current location button
  // 3. QR code button
  // 4. Install button

  const chatButtonActive =
    !hideChatButton &&
    config.enableMapChatbotButton &&
    digitalMap?.display_settings?.show_chatbot_button;
  const qrCodeButtonActive = digitalMap?.display_settings?.show_qr_code_button;
  const installButtonActive =
    config.enableOfflineMode &&
    digitalMap?.pwa_settings?.enable_pwa_installation &&
    (deferredInstallPrompt || isIOS || isPWAInstalled || isStandalone);

  const currentLocationButtonBottom =
    (directionsRoute ? 100 : chatButtonActive ? 76 : 24) + (bottomNavHeight ?? 0);
  const qrCodeButtonBottom = currentLocationButtonBottom + 48;
  const installButtonBottom = qrCodeButtonActive
    ? qrCodeButtonBottom + 48
    : currentLocationButtonBottom + 48;

  const contextValue = React.useMemo(
    () => ({
      displayMode,
      setDisplayMode,
      searchText,
      setSearchText,
      sortBy,
      setSortBy,
      showFilterPane,
      setShowFilterPane,
      searchResultsListPosition,
      setSearchResultsListPosition,
      productInCart,
      setProductInCart,
      savedCartParams,
      setSavedCartParams,
      activeStampPinIdx,
      setActiveStampPinIdx,
      showStampRallyModal: openStampRallyModal,
      setShowStampRallyModal: setOpenStampRallyModal,
      activeCouponViewContext,
      setActiveCouponViewContext,
      menuExpanded,
      setMenuExpanded,
      googleMap,
      openLayersMap,
      setOpenLayersMap,
      activeRestaurantId,
      setActiveRestaurantId,
      activeOpenTableRestaurantId,
      setActiveOpenTableRestaurantId,
      orderStatusRestaurantId,
      setOrderStatusRestaurantId,
      orderStatusRestaurantOrderId,
      setOrderStatusRestaurantOrderId,
      setShouldRefreshCustomerReservations: setShouldFetchCustomerReservations,
      shouldRefreshCustomerReservations: shouldFetchCustomerReservations,
      onGetDirections,
      selectedFloor,
      setSelectedFloor,
      setShouldRefreshCustomerRestaurantOrders: setShouldFetchRestaurantOrders,
      activeIframeUrl,
      setActiveIframeUrl,
      activeItineraryPinKeys,
      setActiveItineraryPinKeys,
      newItinerary,
      setNewItinerary,
      selectedItineraryDate,
      setSelectedItineraryDate,
      showStarredPinList,
      setShowStarredPinList,
      activeItemSelection,
      setActiveItemSelection,
      activeRestaurantPageStack,
      setActiveRestaurantPageStack,
    }),
    [
      displayMode,
      searchText,
      sortBy,
      showFilterPane,
      searchResultsListPosition,
      productInCart,
      savedCartParams,
      activeStampPinIdx,
      openStampRallyModal,
      activeCouponViewContext,
      menuExpanded,
      googleMap,
      openLayersMap,
      activeRestaurantId,
      activeOpenTableRestaurantId,
      orderStatusRestaurantId,
      orderStatusRestaurantOrderId,
      shouldFetchCustomerReservations,
      activeIframeUrl,
      activeItineraryPinKeys,
      newItinerary,
      selectedItineraryDate,
      showStarredPinList,
      activeItemSelection,
      activeRestaurantPageStack,
      onGetDirections,
      selectedFloor,
    ]
  );

  return (
    <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale={i18n.language}>
      <CurrentPositionProvider>
        <MapDisplayContextProvider value={contextValue}>
          <div
            style={
              {
                '--header-height':
                  headerHeight != null
                    ? `${headerHeight}px`
                    : digitalMap?.seo_params?.title
                    ? '93px'
                    : '70px',
                '--bottom-nav-height': bottomNavHeight != null ? `${bottomNavHeight}px` : '0px',
              } as React.CSSProperties
            }
          >
            <MapHead />
            <SavedDisplayStateLoader />
            <SearchContext.Provider
              value={{
                recentQueries: recentSearchQueries,
                setRecentQueries: setRecentSearchQueries,
              }}
            >
              <style jsx global>
                {`
                  html,
                  body {
                    overscroll-behavior: none;
                  }
                `}
              </style>
              {!isOnline && (
                <OpenLayersMap
                  mapId={digitalMap?.id ?? ''}
                  onClickPin={onClickPin}
                  onMapInstanceChange={setOpenLayersMap}
                />
              )}
              {isGoogleMapsLoaded && isOnline ? (
                <GoogleMap
                  id="rich-map"
                  onClick={onClickMap}
                  options={mapDefaultOptions}
                  mapContainerClassName={styles['map-container']}
                  mapContainerStyle={containerStyle}
                  onLoad={onLoad}
                >
                  {walkTimeOverlays.map((overlay) => (
                    <WalkTimeOverlay
                      key={overlay.id}
                      position={overlay.position}
                      walkTime={overlay.walkTime}
                    />
                  ))}
                  {shouldShowOverlay && !digitalMap?.background?.use_overlay_tiling && overlay && (
                    <>
                      <IllustrationOverlay
                        zIndex={102}
                        map={googleMap}
                        url={overlay.image_url}
                        aspectRatio={overlay.image_aspect_ratio}
                        topLeft={overlay.top_left}
                        widthInMeters={overlay.image_projection_width_in_meters}
                        heading={digitalMap?.map_rotation ?? 0}
                      />
                    </>
                  )}
                  {googleMap && <Pins map={googleMap} onClickPin={onClickPin} />}

                  {googleMap && <CurrentPositionMarker map={googleMap} />}

                  {googleMap && digitalMap?.bus_routes && (
                    <BusRoutes routes={digitalMap.bus_routes} />
                  )}
                </GoogleMap>
              ) : null}
              {installButtonActive && (
                <InstallButton
                  style={{
                    position: 'absolute',
                    bottom: `${installButtonBottom}px`,
                    right: '24px',
                  }}
                  deferredInstallPrompt={deferredInstallPrompt}
                  isIOS={isIOS}
                  isPWAInstalled={isPWAInstalled}
                  isStandalone={isStandalone}
                />
              )}
              {digitalMap?.display_settings?.show_qr_code_button && (
                <QRCodeButton
                  style={{
                    position: 'absolute',
                    bottom: `${qrCodeButtonBottom}px`,
                    right: '24px',
                  }}
                />
              )}
              <CurrentLocationButton
                style={{
                  bottom: `${currentLocationButtonBottom}px`,
                }}
              />

              <FloorSelector />

              {isItineraryCreatorEnabled(digitalMap) && (
                <ItineraryFloatingButton
                  selected={showItinerarySideModal || activePage.startsWith('ITINERARIES')}
                  onClick={() => {
                    if (isSmallScreen) {
                      setShowItinerarySideModal((prev) => !prev);
                    } else {
                      setActivePage(
                        activePage.startsWith('ITINERARIES') ? 'MAP' : 'ITINERARIES.LIST'
                      );
                    }
                  }}
                />
              )}

              <StampRallyFetcher />
              {stampRally && (
                <StampModal
                  pin={activeStampPin}
                  onClose={() => {
                    setActiveStampPinIdx(null);
                  }}
                  openStampRallyModal={() => setOpenStampRallyModal(true)}
                  pageId={digitalMap?.id ?? ''}
                  guidanceStampRally={stampRally}
                  itemKey={activeStampPin?.key ?? ''}
                  stampRallyLabel={activeStampPin?.stamp_rally_label ?? ''}
                  stampImageUrl={activeStampPin?.stamp_image_url ?? ''}
                />
              )}
              {stampRally && (
                <StampRallyModal
                  guidanceStampRally={stampRally}
                  open={openStampRallyModal}
                  reservation={existingReservation}
                  onClose={() => setOpenStampRallyModal(false)}
                />
              )}
              {config.enableInstantWin && <InstantWinPopupStampRally />}
              {activeCouponViewContext &&
                (config.enableIAAPADemo ? (
                  <CouponBottomSheet
                    coupon={activeCouponViewContext.coupon}
                    pin={activeCouponViewContext.pin}
                    onClose={() => setActiveCouponViewContext(null)}
                    pageId={digitalMap?.id ?? ''}
                  />
                ) : (
                  <CouponModal
                    coupon={activeCouponViewContext.coupon}
                    pin={activeCouponViewContext.pin}
                    open={true}
                    onClose={() => setActiveCouponViewContext(null)}
                    pageId={digitalMap?.id ?? ''}
                  />
                ))}

              <ItinerarySideModal
                onClose={() => setShowItinerarySideModal(false)}
                open={showItinerarySideModal}
              />

              {activePage.startsWith('ITINERARIES') && <Itineraries />}
              {activePin && pinInfoDisplayStatus !== 'HIDDEN' && (
                <PinDetails
                  key={activePin.index}
                  pin={activePin}
                  onClose={() => {
                    popUrlParams();
                  }}
                />
              )}

              <LocationLogger reservation={existingReservation} />

              {isItineraryCreatorEnabled(digitalMap) && (
                <ItineraryRebaser digitalMapId={digitalMap?.id ?? ''} />
              )}

              {showFilterPane && <FilterPane onClose={() => setShowFilterPane(false)} />}

              {productInCart && <Checkout />}
              {activeRestaurantId && <RestaurantDetails />}
              {orderStatusRestaurantId && <RestaurantDetails initialPage="STATUS" />}
              {activeOpenTableRestaurantId && <OpenTableReservation />}
              {activeIframeUrl && (
                <IframeDisplay url={activeIframeUrl} onClose={() => setActiveIframeUrl(null)} />
              )}
              {displayMode === 'SEARCH_INPUT' && <SearchInput />}
              {activePage === 'SCHEDULE' && <SchedulePane />}
              {activePage === 'ACCOUNT' && <MyAccount />}
              {activePage === 'TICKETS' && <Tickets />}
              {activePage === 'NOTIFICATIONS' && <Notifications />}
              {activePage === 'ORDERS' && <OrdersPage />}
              {showStarredPinList && <StarredPinList />}

              {!hideHeader &&
                (displayMode === 'BASE' || displayMode === 'DESKTOP_SEARCH_RESULTS') && <Header />}
              {activePage === 'MAP' &&
                displayMode !== 'SEARCH_INPUT' &&
                displayMode !== 'SHOW_DIRECTIONS_ROUTE' &&
                !menuExpanded && (
                  <>
                    <SingleCategorySelect />
                  </>
                )}

              {activePage === 'LIST' && <PinList />}
              {displayMode === 'SEARCH_RESULTS' && <SearchResults />}
              {displayMode === 'DESKTOP_SEARCH_RESULTS' && <DesktopSearchResults />}
              {displayMode === 'SHOW_DIRECTIONS_ROUTE' && directionsRoute && (
                <DirectionsFooterBar
                  directionsRoute={directionsRoute}
                  onClickClose={() => {
                    directionsRoute?.outlinePolyline?.setMap(null);
                    directionsRoute?.pathPolyline?.setMap(null);
                    setDirectionsRoute(null);
                    replaceUrlParams({ pin: '', pind: 'HIDDEN', nav: 'map' });
                    setDisplayMode('BASE');
                    (googleMap as any).setHeadingInteractionEnabled(false);
                    (googleMap as any).setHeading(digitalMap?.map_rotation ?? 0);
                  }}
                />
              )}
              {config.enableMapImagePreloading && <MediaPreloader isStandalone={isStandalone} />}

              {/*

            Marketing Automation
            ====================

            Several pieces work together to display marketing automation campaigns on the map:

            1. MarketingAutomationCampaignFetcher is the entry point that fetches instant campaigns for Map-specific events. It does not display anything.
            2. MarketingAutomationDistributor renders the appropriate MA components based on state. It's responsible for displaying popups originating from
               instant campaign fetches, websocket notifications, and web push notifications.
            3. The setMarketingAutomationCampaignReservation() action is dispatched in this file and this reservation is used by MarketingAutomationDistributor
               when displaying campaigns.
            4. setActiveMarketingAutomationCampaign() controls which campaign is currently being displayed. It is dispatched in MarketingAutomationDistributor
               when a new campaign is to be displayed and it is also dispatched from the notifications page when a previously displayed campaign is clicked.

            */}

              <MarketingAutomationCampaignFetcher />

              <WaitTimeUpdateNotifier />

              {!hideChatButton &&
                config.enableMapChatbotButton &&
                digitalMap?.display_settings?.show_chatbot_button &&
                digitalMap && <Chat digitalMapId={digitalMap.id} />}
            </SearchContext.Provider>
          </div>
        </MapDisplayContextProvider>
      </CurrentPositionProvider>
    </LocalizationProvider>
  );
};
